import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, watch, onMounted, onBeforeUnmount, getCurrentInstance, nextTick } from 'vue';
import ClientTopBar from '@/components/views/NewClientView/ClientTopBar.vue';
import EditClientTabs from '@/components/views/NewClientEdit/EditClientTabs.vue';
import Client from '@/models/Client';
import ClientViewSidebar from '@/components/global/sidebar/ClientViewSidebar.vue';
import ClientRepository from '@/repositories/ClientRepository';
import { NewFormData } from '@/interfaces/components/clients/NewFormData';
import { EventBus } from '@/helpers/EventBusHelper';
import { EventBusEvents } from '@/enums/global/EventBusEvents';
import { ClientAdditionalInformation } from '@/interfaces/components/clients/ClientAdditionalInformation';
import ProductGroup from '@/models/ProductGroup';
import ProductGroupRepository from '@/repositories/ProductGroupRepository';
import { UserRightsEnum } from '@/enums/global/UserRights';
import ContactConfig from '@/models/interfaces/Contact';
import ContactPerson from '@/models/ContactPerson';
import { LoadingOverlayHelper } from '@/helpers/LoadingOverlayHelper';
import store from '@/store';
import router from '@/router';


export default /*#__PURE__*/_defineComponent({
  __name: 'NewEditClientView',
  setup(__props, { expose }) {

const { proxy } = getCurrentInstance()!;

function emptyFormData() {
    return {
        initialized: false,
        isCompany: false,
        name: null,
        clientAddresses: [],
        // Legacy address fields for backward compatibility
        addressForm: {
            id: null,
            streetNameAndNumber: null,
            poNumber: null,
            city: null,
            building: null,
            country: null,
            isDefault: true,
        },
        address: null,
        taxId: null,
        registerId: null,
        isTaxPayer: null,
        bankCode: null,
        currency: null,
        paymentType: null,
        priceType: null,
        priceListRegion: null,
        assignedUser: null,
        tags: [''],
        additionalNotes: '',
        contacts: [],
        discounts: [],
        contactPersons: [''],
        discount_ids: [''],
        clientTypes: [],
        sidebarData: {
            relatedFiles: [],
            customerHistory: null,
        },
        webpage: '',
        // bigLogo: null,
        // squareLogo: null,
        roltekAppPartner: false,
        backofficeUser: null,
        leadOrigin: null,
        documentSendingProfile: '',
        generalBusinessPostingGroup: '',
        customerPostingGroup: '',
        nonResidentFinancialSector: '',
        capitalConnection: '',
        financialSector: '',
        paymentTerms: '',
        acceptedBankAccount: '',
        location: '',
        shipmentMethod: '', 
        transactionType: '',
        transportationType: '',
        blockedOrders: false,
        blockedDelivery: false,
        blockedProduction: false,
        overpaymentAmount: 0,
        openAmount: 0,
        limitAmount: 0,
        controlType: undefined,
    };
}

const clientId = ref<null | string>(null);
const client = ref<any | null>(null);
const loadingOverlay = new LoadingOverlayHelper(proxy, {});
const formData = ref<NewFormData>({ ...emptyFormData() });
const initialLoading = ref(true);

const userRights = computed(() => store.getters['jwtData/userRights']);
const isItalyUG = computed(() => store.getters['jwtData/isItalyUG']);
const canAccessLogistics = computed(() => userRights.value.includes(UserRightsEnum.ACCESS_LOGISTICS));
const isClientExisting = computed(() => client.value !== null);
const canEditClient = computed(() => userRights.value.includes(UserRightsEnum.EDIT_CLIENT));
const isSaveButtonDisabled = computed(() => !canEditClient.value);

function parseB2bDeliveryDays(value: unknown): string[] {
    if (typeof value === 'string') {
        return value.split(' | ').map((day: string) => day.trim());
    }
    if (Array.isArray(value)) {
        return value;
    }
    return [];
}

function emptyAddress() {
    return {
        streetNameAndNumber: '',
        poNumber: '',
        city: '',
        building: '',
        country: undefined,
        addressType: '',
        b2bDeliveryPhone: '',
        b2bDeliveryEmail: '',
        b2bDeliveryNotes: '',
        b2bDeliveryDays: [],
    };
}

const defaultClientAddress = computed(() => {
    const selectedAddress = formData.value.clientAddresses?.find((address: any) => address.isDefault) || emptyAddress();

    return { ...selectedAddress, isDefault: false, addressType: selectedAddress.addressType || '' };
});

function setDefaultContactAsFirst() {
    const contacts = client.value.contactPersons.slice();

    const defaultContactIndex = contacts.findIndex((contact: any) => contact.isDefault);

    if (defaultContactIndex < 0) {
        contacts[0].isDefault = true;
    } else {
        contacts.unshift(contacts.splice(defaultContactIndex, 1)[0]);
    }

    contacts.forEach((contact: any) => {
        if (contact.address) {
            contact.address.b2bDeliveryDays = parseB2bDeliveryDays(contact.address.b2bDeliveryDays);
        }
    });

    formData.value.contacts = contacts;
}

function serializeDataOnLoad() {
    formData.value = { ...emptyFormData() };

    if (client.value === null) {
        return;
    }

    setDefaultContactAsFirst();

    if (!client.value.isCompany) {
        formData.value.isCompany = false;
    } else {
        formData.value.isCompany = true;
        formData.value.bankCode = client.value.bankCode;
        formData.value.currency = client.value.currency_id;
        formData.value.paymentType = client.value.paymentType_id;
    }

    if (client.value.isCompany || isItalyUG.value) {
        formData.value.taxId = client.value.taxId;
        formData.value.registerId = client.value.registerId;
        formData.value.isTaxPayer = client.value.isTaxPayer;
    }

    formData.value.name = client.value.name;
    formData.value.documentSendingProfile = client.value.documentSendingProfile ?? '';

    // Populate clientAddresses from client's addresses
    formData.value.clientAddresses = [];
    if (client.value?.addresses && client.value.addresses.length > 0) {
        formData.value.clientAddresses = client.value.addresses.map((addr: any) => {
            const deliveryDays = parseB2bDeliveryDays(addr.b2bDeliveryDays);
            return {
                id: addr?.id,
                streetNameAndNumber: addr?.streetNameAndNumber,
                poNumber: addr?.poNumber,
                city: addr?.city,
                building: addr?.building,
                country: addr?.country?.id ?? addr?.country,
                addressType: addr?.addressType?.id,
                isDefault: addr?.isDefault ?? false,
                b2bDeliveryPhone: addr?.b2bDeliveryPhone,
                b2bDeliveryEmail: addr?.b2bDeliveryEmail,
                b2bDeliveryNotes: addr?.b2bDeliveryNotes,
                b2bDeliveryDays: deliveryDays,
                languageCode: addr?.languageCode || '',
                printRegion: addr?.printRegion || '',   
            };
        });
    } else if (client.value?.address) {
        // Legacy address fields for backward compatibility
        const deliveryDays = parseB2bDeliveryDays(client.value.address?.b2bDeliveryDays);
        formData.value.clientAddresses = [
            {
                id: client.value?.address?.id,
                streetNameAndNumber: client.value?.address?.streetNameAndNumber,
                poNumber: client.value?.address?.poNumber,
                city: client.value?.address?.city,
                building: client.value?.address?.building,
                country: client.value?.address?.country?.id,
                isDefault: true,
                b2bDeliveryPhone: client.value?.address?.b2bDeliveryPhone,
                b2bDeliveryEmail: client.value?.address?.b2bDeliveryEmail,
                b2bDeliveryNotes: client.value?.address?.b2bDeliveryNotes,
                b2bDeliveryDays: deliveryDays,
                languageCode: client.value.address?.languageCode || '',
                printRegion: client.value.address?.printRegion || '',
            },
        ];
    }

    // Set the first address as default if none are marked as default to ensure there's always a default address
    const isOneAddressDefault = formData.value.clientAddresses.some((address) => address.isDefault);
    if (!isOneAddressDefault && formData.value.clientAddresses.length > 0) {
        formData.value.clientAddresses[0].isDefault = true;
    }

    formData.value.address = null;
    formData.value.priceType = client.value.priceType_id;
    formData.value.priceListRegion = client.value.priceListRegion_id;
    if (client.value.discounts.length > 0) {
        formData.value.discounts = client.value.discounts.map((discount: any) => {
            return {
                id: discount.id,
                name: discount.discountedEntity.name,
                discountCategory: discount.discountType.toUpperCase(),
                discountedEntityOid: discount.discountedEntityOid ? discount.discountedEntityOid : discount.oid,
                productGroup: discount.productGroup_id,
                isEditing: false,
                isPercentage: discount.isPercentage,
                pc1: discount.price1[0],
                pc2: discount.price2[0],
            };
        });
    }

    formData.value.generalBusinessPostingGroup = client.value.generalBusinessPostingGroup ?? '';
    formData.value.customerPostingGroup = client.value.customerPostingGroup ?? '';
    formData.value.nonResidentFinancialSector = client.value.nonResidentFinancialSector ?? '';
    formData.value.capitalConnection = client.value.capitalConnection ?? '';
    formData.value.financialSector = client.value.financialSector ?? '';
    formData.value.paymentTerms = client.value.paymentTerms ?? '';
    formData.value.acceptedBankAccount = client.value.acceptedBankAccount ?? '';
    formData.value.location = client.value.location ?? '';
    formData.value.shipmentMethod = client.value.shipmentMethod ?? '';
    formData.value.transactionType = client.value.transactionType ?? '';
    formData.value.transportationType = client.value.transportationType ?? '';

    formData.value.blockedOrders = client.value.blockedOrders ?? false;
    formData.value.blockedDelivery = client.value.blockedDelivery ?? false;
    formData.value.blockedProduction = client.value.blockedProduction ?? false;
    formData.value.overpaymentAmount = client.value.overpaymentAmount ?? 0;
    formData.value.openAmount = client.value.openAmount ?? 0;
    formData.value.limitAmount = client.value.limitAmount ?? 0;
    formData.value.controlType = client.value.controlType ?? undefined;

    formData.value.assignedUser = client.value?.assignedUser?.id ?? '';
    formData.value.additionalNotes = client.value?.additionalNotes ?? '';
    formData.value.tags = client.value.clientType_ids.slice();
    formData.value.webpage = client.value?.webpage ?? '';
    // formData.value.bigLogo = client.value?.bigLogo;
    // formData.value.squareLogo = client.value?.squareLogo;
    formData.value.roltekAppPartner = client.value?.roltekAppPartner;
    formData.value.backofficeUser = client.value?.backofficeUser?.id ?? '';
    formData.value.leadOrigin = client.value?.origin?.id ?? '';
    formData.value.initialized = true;
}

function addDefaultContactBasedOnCustomerData() {
    const contactData = {
        id: undefined,
        name: formData.value.name || undefined,
        email: '',
        phone: '',
        job: undefined,
        address: { ...defaultClientAddress.value },
        birthday: '',
        isDefault: true,
        default: true,
        useDefaultAddress: true,
    };

    formData.value.contacts.push(contactData as any);
}

async function fetchNecessaryConctactsDataAndSetDefaultValues() {
    if (!clientId.value) {
        return;
    }

    const clientData = ClientRepository.getById(clientId.value);

    // Wait for all contact data to be fetched before setting useDefaultAddress
    if (clientData?.contactPersons?.length) {
        await Promise.all(clientData.contactPersons.map((contact: ContactPerson) => ContactPerson.getById(contact.id)));
    }

    // If we have existing contacts with no address for some reason, we need to set it up
    // for proper functionality of the form
    await nextTick();

    // TODO fix typing here
    // formData.value.contacts.forEach((contact: ContactConfig) => {
    formData.value.contacts.forEach((contact: any) => {
        if (!contact.address) {
            proxy.$set(contact, 'address', { ...emptyAddress() });
        }

        const matchedAddress = formData.value.clientAddresses?.find(
            (address: any) => contact.address && address.id === contact.address.id,
        );

        if (contact.address && matchedAddress) {
            proxy.$set(contact, 'useDefaultAddress', true);

            if (!contact.address?.addressType) {
                proxy.$set(contact.address, 'addressType', matchedAddress?.addressType || '');
            }
        } else {
            proxy.$set(contact, 'useDefaultAddress', false);
        }
    });
}

async function fetchClient(isUpdating: boolean = false) {
    client.value = null;
    clientId.value = router.currentRoute.path.includes('client/new') ? null : router.currentRoute.params.clientId;

    if (clientId.value !== null) {
        const existingProductGroups = ProductGroupRepository.getAll();

        await Client.getById(clientId.value);

        if (existingProductGroups.length > 0 && isUpdating) {
            // Reset side effect of product groups being changed after updating discounts
            // This is needed to ensure that the product groups are up-to-date when the client is loaded
            ProductGroup.create({ data: existingProductGroups });
        }
    }

    client.value = ClientRepository.getById(clientId.value);
    serializeDataOnLoad();

    // Add default contact if no contacts exist
    if (!formData.value.contacts.length) {
        addDefaultContactBasedOnCustomerData();
    }

    // Fetch full contact data and set useDefaultAddress values
    await fetchNecessaryConctactsDataAndSetDefaultValues();

    await nextTick();
    EventBus.$emit('discountsUpdated');

    return Promise.resolve();
}

function updateClientData(data: ClientAdditionalInformation) {
    // loop through values and update the form data with the new values if there are fields to update
    for (const key in data.values) {
        if (data.fields.includes(key)) {
            switch (key) {
                case 'city':
                    formData.value.addressForm!.city = data.values[key];
                    break;
                case 'country':
                    formData.value.addressForm!.country = String(data.values[key]);
                    break;
                case 'fullName':
                case 'shortName':
                    formData.value.name = data.values[key];
                    break;
                case 'postalCode':
                    formData.value.addressForm!.poNumber = data.values[key];
                    break;
                case 'street':
                    formData.value.addressForm!.streetNameAndNumber = data.values[key];
                    break;
                case 'registerID':
                    formData.value.registerId = data.values[key];
                    break;
                case 'taxNumber':
                    formData.value.taxId = data.values[key];
                    break;
                case 'taxPayer':
                    formData.value.isTaxPayer = data.values[key] ? true : false;
                    break;
                case 'documentSendingProfile':
                    formData.value.documentSendingProfile = data.values[key];
                    break;
            }
        }
    }
}

// created
(function () {
    EventBus.$on(EventBusEvents.updateClientData, updateClientData);
})();

onBeforeUnmount(() => {
    EventBus.$off(EventBusEvents.updateClientData, updateClientData);
});

onMounted(async () => {
    try {
        loadingOverlay.start();
        await fetchClient();
    } catch (error) {
    } finally {
        initialLoading.value = false;
        loadingOverlay.stop();
    }
});

watch(
    () => router.currentRoute,
    async () => {
        await fetchClient();
    },
);

watch(
    () => formData.value.isCompany,
    (newVal: boolean, oldVal: boolean) => {
        if (!formData.value.initialized) {
            return;
        }

        if (!newVal) {
            formData.value.webpage = null;
        }
    },
);

expose({ canEditClient });

return { __sfc: true,proxy, emptyFormData, clientId, client, loadingOverlay, formData, initialLoading, userRights, isItalyUG, canAccessLogistics, isClientExisting, canEditClient, isSaveButtonDisabled, parseB2bDeliveryDays, emptyAddress, defaultClientAddress, setDefaultContactAsFirst, serializeDataOnLoad, addDefaultContactBasedOnCustomerData, fetchNecessaryConctactsDataAndSetDefaultValues, fetchClient, updateClientData, ClientTopBar, EditClientTabs, ClientViewSidebar }
}

})